import {
  constructTable,
  tableFeatures,
  columnFilteringFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_includesString,
} from "@tanstack/table-core";
import { storeReactivityBindings } from "@tanstack/table-core/store-reactivity-bindings";

import { createElement, isFormControl, replaceChildren, uniqueId } from "../utils";

const features = tableFeatures({
  coreReactivityFeature: storeReactivityBindings(),
  columnFilteringFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { includesString: filterFn_includesString },
});

/**
 * A sortable, filterable table component.
 */
export default class Table extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-table") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * @type {Map<Node, Node>}
   */
  rowTemplates = new Map();

  /**
   * @type {import('@tanstack/table-core').Table<typeof features, Record<string, string>>}
   */
  model = /** @type {any} */ (undefined);

  /**
   * Sets up the component on mount.
   */
  connectedCallback() {
    this.setup();
    this.render();

    /**
     * A generic event handler that manages sort and filter updates.
     *
     * @param {Event} event A generic event dispatched by a form control.
     */
    const handleFormChange = (event) => {
      if (event.type === "submit") {
        event.preventDefault();
      }

      if (event.type === "reset") {
        this.model.setColumnFilters([]);
        this.model.setSorting([]);
        return;
      }

      const form = event.currentTarget;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const formValues = Array.from(form.elements)
        .filter(isFormControl)
        .map((el) => ({ id: el.name, value: el.value }));

      const filters = this.model.store.state.columnFilters
        .filter(({ id }) => !formValues.some((o) => o.id === id))
        .concat(formValues)
        .filter((o) => !!String(o.value).trim());

      this.model.setColumnFilters(filters);
    };

    this.querySelectorAll("form[method='filter']").forEach((el) => {
      el.addEventListener("change", handleFormChange);
      el.addEventListener("reset", handleFormChange);
      el.addEventListener("submit", handleFormChange);
      this.cleanup.add(() => {
        el.removeEventListener("change", handleFormChange);
        el.removeEventListener("reset", handleFormChange);
        el.removeEventListener("submit", handleFormChange);
      });
    });
  }

  /**
   * Cleans up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Updates the table UI.
   */
  render() {
    const thead = this.querySelector("thead") || this.querySelectorAll("[role='rowgroup']").item(0);

    const tbody = this.querySelector("tbody") || this.querySelectorAll("[role='rowgroup']").item(1);

    if (!thead || !tbody) {
      this.innerHTML = "Something went wrong. Failed to update table component.";
      return;
    }

    /**
     * @param {import("@tanstack/table-core").Header<typeof features, Record<string, string>>} header The table header
     * @returns {string|Node} The header content
     */
    const getHeaderContent = (header) => {
      /** @type {string|Node} */
      let content = header.isPlaceholder
        ? ""
        : flexRender(header.column.columnDef.header ?? "", header.getContext());
      if (header.column.getCanSort()) {
        const sortDir = header.column.getIsSorted();

        /** @param {Event} event */
        const onclick = (event) => header.column.getToggleSortingHandler()?.(event);

        const indicators = { asc: "▴", desc: "▾" };

        content = createElement("button", { onclick }, [
          createElement("span", {}, content),
          createElement(
            "span",
            { "aria-label": "Remove filter" },
            sortDir ? indicators[sortDir] : "",
          ),
        ]);
      }
      return content;
    };

    replaceChildren(
      thead,
      this.model.getHeaderGroups().map((group) => {
        const template = this.rowTemplates.get(thead);
        if (template) {
          const row = template.cloneNode(true);
          if (row instanceof HTMLElement) {
            replaceChildren(
              row,
              group.headers.map((header, index) => {
                const cell = row.children.item(index);
                if (!cell) return "";
                replaceChildren(cell, getHeaderContent(header));
                return cell;
              }),
            );
            return row;
          }
        }
        return "";
      }),
    );

    const rows = this.model.getRowModel().rows;

    if (rows.length < 1) {
      tbody.innerHTML = "";
    } else {
      replaceChildren(
        tbody,
        rows.map((row) => {
          /** @type {string|Node} */
          let el = "";
          const template = this.rowTemplates.get(tbody);
          if (template) {
            el = template.cloneNode(true);
            if (el instanceof HTMLElement) {
              const children = el.children;
              replaceChildren(
                el,
                row.getVisibleCells().map((cell, index) => {
                  const clone = children.item(index);
                  if (!clone) return "";
                  replaceChildren(
                    clone,
                    flexRender(cell.column.columnDef.cell ?? "", cell.getContext()),
                  );
                  return clone;
                }),
              );
            }
          }
          return el;
        }),
      );
    }

    const filters = this.querySelector("form[method='filter']");

    if (filters) {
      const activeFilters = this.model.store.state.columnFilters;

      filters.querySelectorAll("input, select").forEach((el) => {
        if (isFormControl(el)) {
          const filter = activeFilters.find((o) => o.id === el.name);
          el.value = filter ? `${filter.value}` : "";
        }
      });

      const output = filters.querySelector("output");

      if (output) {
        const active = this.model.store.state.columnFilters;
        if (active.length > 0) {
          const tags = active.map((filter) => {
            const column = this.model.getColumn(filter.id)?.columnDef?.header;
            const content = column ? `${column}: ${filter.value}` : `${filter.value}`;
            return `<li data-filter-id="${filter.id}">${content}</li>`;
          });

          const label = createElement("span", {
            id: `${this.id}-active-filters`,
            textContent: "The following filters are applied: ",
          });

          const taglist = createElement("ifrs-taglist", {
            "aria-describedby": label.id,
            innerHTML: `<ul>${tags}</ul>`,
          });

          taglist.addEventListener("dismiss", (event) => {
            if (event.target instanceof HTMLElement && event.target.dataset.filterId) {
              const id = event.target.dataset.filterId;
              const filters = this.model.store.state.columnFilters.filter((o) => o.id !== id);
              this.model.setColumnFilters(filters);
            }
          });

          replaceChildren(output, label, taglist);
        } else {
          replaceChildren(output, "");
        }
      }
    }
  }

  /**
   * Initializes the component properties and markup.
   *
   * The function may be called repeatedly and should be idempotent.
   */
  setup() {
    if (!this.id) {
      this.id = uniqueId();
    }

    if (!this.model) {
      try {
        /** @type {import('@tanstack/table-core').ColumnDef<typeof features, Record<string, string>>[]} */
        const columns = [];
        /** @type {Record<string, string>[]} */
        const data = [];

        this.querySelectorAll("thead, tbody, [role='rowgroup']").forEach((group) => {
          group.querySelectorAll("tr, [role='row']").forEach((row) => {
            if (!this.rowTemplates.has(group)) {
              this.rowTemplates.set(group, row.cloneNode(true));
            }

            row
              .querySelectorAll(":where(th, td):is(thead *), [role='columnheader']")
              .forEach((header) => {
                if (header instanceof HTMLElement && header.dataset.columnKey) {
                  columns.push({
                    header: header.textContent.trim(),
                    accessorKey: header.dataset.columnKey,
                  });
                }
              });

            const cells = row.querySelectorAll(
              ":where(th, td):is(tbody *), [role='rowheader'], [role='cell']",
            );

            if (cells.length > 0) {
              data.push(
                Object.fromEntries(
                  /** @type {[string, string][]} */ (
                    Array.from(cells)
                      .map(
                        (cell) =>
                          cell instanceof HTMLElement &&
                          cell.dataset.columnKey && [
                            cell.dataset.columnKey,
                            cell.textContent.trim(),
                          ],
                      )
                      .filter(Boolean)
                  ),
                ),
              );
            }
          });
        });

        this.model = constructTable({ features, columns, data });
        const sub = this.model.store.subscribe(() => this.render());
        this.cleanup.add(() => sub.unsubscribe());
      } catch (error) {
        throw new Error("Couldn't parse table data", { cause: error });
      }
    }
  }
}

/**
 * Renders table cell contents.
 *
 * @see {@link https://tanstack.com/table/latest/docs/guide/cells#cell-rendering}
 *
 * @param {string|((arg: any) => any)} component The cell component or content
 * @param {any} props The cell context
 * @returns {string} The rendered cell content
 */
function flexRender(component, props) {
  if (typeof component === "function") {
    return component(props);
  }
  return component;
}
